
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  Ref,
  watch
} from "vue";
import ListPicker from "../modals/ListPicker.vue";
import CloseHeader from "../table/CloseHeader.vue";
import DataTable from "../data-table/DataTable.vue";
import Cell from "../data-table/Cell.vue";
import OrderTableHeader from "../data-table/OrderTableHeader.vue";
import PersonPhoto from "../data-table/PersonPhoto.vue";
import TableHeader from "../data-table/TableHeader.vue";
import RelationHeader from "../table/RelationHeader.vue";
import Row from "../data-table/Row.vue";
import { search } from "../data-table/table-order-utils";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import {
  clearAllQueries,
  removeQueries,
  updateQuery
} from "../../../utils/routing_utils";
import { getOrderFromURL } from "../../../utils/order_utils";
import { wrapLoading } from "../../../utils/wrap_loading";
import { dispatchError } from "../../../utils/error_handling";
import { User } from "domain/entities/entities";
import Pagination from "../../../components/application/shop/PaginationDefault.vue";
import { Order } from "domain/entities/order";
import { dispatchSuccessBox } from "../../mixins";
import SelectFilter from "../data-table/SelectFilter.vue";
import http from "../../../../repository/http";

export default defineComponent({
  components: {
    SelectFilter,
    ListPicker,
    CloseHeader,
    RelationHeader,
    OrderTableHeader,
    PersonPhoto,
    DataTable,
    TableHeader,
    Pagination,
    Row,
    Cell
  },
  setup() {
    /** Instance of the vuex store. */
    const store = useStore();

    /** Instance of a Route which contains relevant information about the current route. */
    const route = useRoute();

    /** Instance of a router. */
    const router = useRouter();

    /** Array of IDs containing all the current selected users. */
    const selectedIDs: Ref<Set<number>> = ref(new Set());

    const numberOfPages: Ref<number> = ref(0);

    const loadedPages: Record<number, User[]> = {};

    /** Tells whether the list will allow the interest to select more than one item. */
    const allowMultiple: ComputedRef<boolean> = computed(() => {
      return store.getters["list_picker_module/allowMultiple"];
    });

    onBeforeMount(async () => {
      await wrapLoading(async () => {
        const order = getOrderFromURL(route);
        await listUsers(order);
        numberOfPages.value = store.getters["userProfile_module/userPages"];
      });
    });

    onBeforeUnmount(async () => {
      await clearAllQueries(router);
    });

    /** Closes the modal discarding all the changes. */
    const close = async (): Promise<void> => {
      await store.dispatch("list_picker_module/close");
    };

    /** Variable to get the user data. */
    const user = store.getters["userProfile_module/user"];

    /** Saves all the changes and then closes the modal. */
    const save = async (): Promise<void> => {
      const updatedUsers: User[] = await getSelectedUsers(
        loadedPages,
        selectedIDs.value
      );

      const userIDs = updatedUsers.map(user => user.id);
      const courseID = Number(route.params.course_id);

      await indicationTrainingUser(userIDs, courseID);

      await dispatchSuccessBox("Você indicou este treinanamento com sucesso!");

      await store.dispatch("list_picker_module/save", updatedUsers);

      await close();
    };

    const users: Ref<User[]> = ref([]);

    const listUsers = async (order: Order): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("userProfile_module/listUsers", order);
          users.value = store.getters["userProfile_module/users"];
          loadedPages[order.page] = users.value;
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const indicationTrainingUser = async (
      userIDs: number[],
      courseID: number
    ): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("userProfile_module/indicationTrainingUser", {
            courseID: courseID,
            userIDs: userIDs
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    watch(
      () => route.query,
      async () => {
        const order = getOrderFromURL(route);

        if (route.query.filterBy) {
          numberOfPages.value = 1;
          await removeQueries(router, ["page", "orderBy", "orderBy", "search"]);
          users.value = await getSelectedUsers(loadedPages, selectedIDs.value);
        } else {
          await listUsers(order);
          numberOfPages.value = store.getters["userProfile_module/userPages"];
        }
      }
    );

    const getSelectedUsers = async (
        _loadedPages: Record<number, User[]>,
        _selectedIDs: Set<number>
    ): Promise<User[]> => {
      let allLoadedUsers: User[] = [];

      for (let key in _loadedPages) {
        for (let user of _loadedPages[key]) {
          allLoadedUsers.push(user);
        }
      }

      const updatedUsers: User[] = allLoadedUsers.filter((user: User) =>
          _selectedIDs.has(user.id)
      );

      for (let id of Array.from(_selectedIDs)) {
        if (!allLoadedUsers.map((user: User) => user.id).includes(id)) {
          const response = await http.get(`/api/manager/user/list/${id}`);
          const user: User = response.data.user;
          updatedUsers.push(user);
        }
      }

      return updatedUsers;
    };

    const checkRow = (user: User) => {
      if (!allowMultiple.value && hasSelectedItem(user)) {
        return;
      }
      const input = document.getElementById(
        `user${user.id}`
      ) as HTMLInputElement;
      input.checked = !input.checked;
      input.dispatchEvent(new Event("change"));
    };

    const hasSelectedItem = (user: User): boolean => {
      const isUserOnTheList = selectedIDs.value.has(user.id);
      const hasAtLeastOneSelected = selectedIDs.value.size >= 1;
      return hasAtLeastOneSelected && !isUserOnTheList;
    };

    const toggleUserSelection = (user: User) => {
      if (selectedIDs.value.has(user.id)) {
        selectedIDs.value.delete(user.id);
      } else {
        selectedIDs.value.add(user.id);
      }
    };

    /** Sets the current height of the DataTable. */
    const tableHeight = ref(
      window.innerWidth > 1000 ? "calc(100% - 180px)" : "calc(100% - 230px)"
    );

    window.addEventListener("resize", () => {
      tableHeight.value =
        window.innerWidth > 1000 ? "calc(100% - 180px)" : "calc(100% - 230px)";
    });

    const showPermissionColumn = ref(window.innerWidth > 1000);

    const handleResize = () => {
      showPermissionColumn.value = window.innerWidth > 1000;
    };

    window.addEventListener("resize", handleResize);

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
    });

    return {
      showPermissionColumn,
      numberOfPages,
      allowMultiple,
      selectedIDs,
      tableHeight,
      users,
      toggleUserSelection,
      hasSelectedItem,
      updateQuery,
      checkRow,
      search,
      close,
      save
    };
  }
});
